/**
 * 플랫폼 관리 페이지
 * Level5 전용 - 플랫폼 전체 관리
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  MonitorHeart as MonitorIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { getLevelNumber } from '@/config/menuConfig';
import { platformAPI } from '@/services/api';

export default function PlatformPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTeams: 0,
    totalCompanies: 0,
    activeUsers: 0,
    totalFiles: 0,
    todayUsers: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [llmUsage, setLlmUsage] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // 다이얼로그 상태
  const [companiesDialog, setCompaniesDialog] = useState(false);
  const [llmDialog, setLlmDialog] = useState(false);
  const [monitoringDialog, setMonitoringDialog] = useState(false);

  const isAdmin = !!(user?.isAdmin || user?.role === 'admin' || getLevelNumber(user) >= 5);

  useEffect(() => {
    if (!isAdmin) {
      setSnackbar({
        open: true,
        message: '플랫폼 관리 페이지는 관리자 전용입니다.',
        severity: 'error'
      });
      return;
    }
    loadData();
  }, [isAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, recentUsersRes, llmRes, monitorRes] = await Promise.all([
        platformAPI.getStats(),
        platformAPI.getRecentUsers({ limit: 10 }),
        platformAPI.getLLMUsage({ days: 30 }),
        platformAPI.getMonitoring()
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      if (recentUsersRes.data.success) {
        setRecentUsers(recentUsersRes.data.data.users || []);
      }
      if (llmRes.data.success) {
        setLlmUsage(llmRes.data.data);
      }
      if (monitorRes.data.success) {
        setMonitoring(monitorRes.data.data);
      }
    } catch (error) {
      console.error('Load platform data error:', error);
      setSnackbar({
        open: true,
        message: '데이터를 불러오는데 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const res = await platformAPI.getCompanies();
      if (res.data.success) {
        setCompanies(res.data.data.companies || []);
      }
    } catch (error) {
      console.error('Load companies error:', error);
    }
    setCompaniesDialog(true);
  };

  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success';
      case 'disconnected': return 'error';
      default: return 'default';
    }
  };

  const getServiceStatusIcon = (status) => {
    return status === 'connected' 
      ? <CheckCircleIcon fontSize="small" color="success" />
      : <CancelIcon fontSize="small" color="error" />;
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            접근 권한이 없습니다
          </Typography>
          <Typography variant="body1" color="text.secondary">
            플랫폼 관리 페이지는 관리자 전용입니다.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            플랫폼 관리
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            플랫폼 전체 통계 및 관리
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          새로고침
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* 통계 카드 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      전체 사용자
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalUsers}
                    </Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      활성 사용자
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {stats.activeUsers}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      전체 팀
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalTeams}
                    </Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      등록 기업
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalCompanies}
                    </Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 기업 관리 */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    기업 관리
                  </Typography>
                  <BusinessIcon color="primary" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  등록된 기업 수: {stats.totalCompanies}개
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  기업별 사용자 및 팀 관리 기능은 준비 중입니다.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={loadCompanies}>
                  기업 목록 보기
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* LLM 사용량 */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    LLM 사용량
                  </Typography>
                  <SpeedIcon color="primary" />
                </Box>
                {llmUsage ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      LLM API 호출 통계 및 사용량 모니터링
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        총 세션: <strong>{llmUsage.totalSessions?.toLocaleString()}</strong>건
                      </Typography>
                      <Typography variant="body2">
                        총 메시지: <strong>{llmUsage.totalMessages?.toLocaleString()}</strong>건
                      </Typography>
                      <Typography variant="body2">
                        총 토큰: <strong>{llmUsage.totalTokens?.toLocaleString() || '0'}</strong>
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    사용량 데이터를 불러오는 중...
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => setLlmDialog(true)}>
                  사용량 상세 보기
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* 시스템 모니터링 */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    시스템 모니터링
                  </Typography>
                  <MonitorIcon color="primary" />
                </Box>
                {monitoring ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      서버 상태, 데이터베이스 연결, API 응답 시간 등
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Object.entries(monitoring.services || {}).map(([name, status]) => (
                        <Chip
                          key={name}
                          icon={getServiceStatusIcon(status)}
                          label={name}
                          size="small"
                          variant="outlined"
                          color={getServiceStatusColor(status)}
                        />
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      업타임: {monitoring.server?.uptimeFormatted || '-'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    모니터링 데이터를 불러오는 중...
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => setMonitoringDialog(true)}>
                  모니터링 대시보드
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* 최근 가입 사용자 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    최근 가입 사용자
                  </Typography>
                  <PeopleIcon color="primary" />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>이메일</TableCell>
                        <TableCell>이름</TableCell>
                        <TableCell>접근 레벨</TableCell>
                        <TableCell>상태</TableCell>
                        <TableCell>가입일</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              최근 가입한 사용자가 없습니다.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        recentUsers.map((u) => (
                          <TableRow key={u.id}>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>{u.name || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={u.accessLevel || 'Level1'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={u.status === 'active' ? '활성' : u.status === 'pending' ? '대기' : '비활성'}
                                size="small"
                                color={u.status === 'active' ? 'success' : u.status === 'pending' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {u.createdAt
                                ? new Date(u.createdAt).toLocaleDateString('ko-KR')
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
              <CardActions>
                <Button size="small" href="/users">
                  전체 사용자 보기
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 기업 목록 다이얼로그 */}
      <Dialog open={companiesDialog} onClose={() => setCompaniesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>기업 목록</DialogTitle>
        <DialogContent>
          {companies.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              등록된 기업이 없습니다.
            </Typography>
          ) : (
            <List>
              {companies.map((company, idx) => (
                <Box key={company.companyId}>
                  {idx > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={`기업 ID: ${company.companyId}`}
                      secondary={`사용자 ${company.totalUsers}명 (활성: ${company.activeUsers}명)`}
                    />
                  </ListItem>
                  {company.users && company.users.length > 0 && (
                    <Box sx={{ pl: 4, pb: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>이메일</TableCell>
                            <TableCell>이름</TableCell>
                            <TableCell>레벨</TableCell>
                            <TableCell>상태</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {company.users.slice(0, 5).map(u => (
                            <TableRow key={u.id}>
                              <TableCell>{u.email}</TableCell>
                              <TableCell>{u.name || '-'}</TableCell>
                              <TableCell>{u.accessLevel}</TableCell>
                              <TableCell>
                                <Chip
                                  label={u.status === 'active' ? '활성' : '비활성'}
                                  size="small"
                                  color={u.status === 'active' ? 'success' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                          {company.users.length > 5 && (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Typography variant="caption" color="text.secondary">
                                  외 {company.users.length - 5}명
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompaniesDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* LLM 사용량 다이얼로그 */}
      <Dialog open={llmDialog} onClose={() => setLlmDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>LLM 사용량 상세</DialogTitle>
        <DialogContent>
          {llmUsage ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">총 세션</Typography>
                    <Typography variant="h6">{llmUsage.totalSessions?.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">총 메시지</Typography>
                    <Typography variant="h6">{llmUsage.totalMessages?.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">총 토큰</Typography>
                    <Typography variant="h6">{llmUsage.totalTokens?.toLocaleString() || '0'}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">최근 30일 세션</Typography>
                    <Typography variant="h6">{llmUsage.recentSessions?.toLocaleString()}</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* 모델별 사용량 */}
              {llmUsage.modelUsage && llmUsage.modelUsage.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    모델별 사용량 (최근 30일)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>모델</TableCell>
                          <TableCell align="right">세션 수</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {llmUsage.modelUsage.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.model || '(미지정)'}</TableCell>
                            <TableCell align="right">{parseInt(item.sessionCount).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* 일별 사용량 */}
              {llmUsage.dailyUsage && llmUsage.dailyUsage.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    일별 사용량 (최근 7일)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>날짜</TableCell>
                          <TableCell align="right">메시지 수</TableCell>
                          <TableCell align="right">토큰</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {llmUsage.dailyUsage.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.date}</TableCell>
                            <TableCell align="right">{parseInt(item.messageCount).toLocaleString()}</TableCell>
                            <TableCell align="right">{parseInt(item.tokens || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              데이터를 불러오는 중...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLlmDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 시스템 모니터링 다이얼로그 */}
      <Dialog open={monitoringDialog} onClose={() => setMonitoringDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>시스템 모니터링 대시보드</DialogTitle>
        <DialogContent>
          {monitoring ? (
            <Box>
              {/* 서비스 상태 */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                서비스 상태
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {Object.entries(monitoring.services || {}).map(([name, status]) => (
                  <Grid item xs={6} sm={3} key={name}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      {getServiceStatusIcon(status)}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{name}</Typography>
                      <Chip
                        label={status}
                        size="small"
                        color={getServiceStatusColor(status)}
                        sx={{ mt: 0.5 }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* 서버 정보 */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                서버 정보
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">업타임</Typography>
                    <Typography variant="body1">{monitoring.server?.uptimeFormatted || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Node.js 버전</Typography>
                    <Typography variant="body1">{monitoring.server?.nodeVersion || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">플랫폼</Typography>
                    <Typography variant="body1">{monitoring.server?.platform || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">CPU 부하 (1m/5m/15m)</Typography>
                    <Typography variant="body1">
                      {monitoring.server?.cpu?.loadAvg1m || '0'} / {monitoring.server?.cpu?.loadAvg5m || '0'} / {monitoring.server?.cpu?.loadAvg15m || '0'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* 메모리 사용량 */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                메모리 사용량
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Heap 사용</Typography>
                    <Typography variant="body2">
                      {monitoring.server?.memory?.heapUsed || 0} MB / {monitoring.server?.memory?.heapTotal || 0} MB
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={monitoring.server?.memory?.heapTotal 
                      ? (monitoring.server.memory.heapUsed / monitoring.server.memory.heapTotal) * 100 
                      : 0}
                    sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">RSS: {monitoring.server?.memory?.rss || 0} MB</Typography>
                  <Typography variant="body2">External: {monitoring.server?.memory?.external || 0} MB</Typography>
                </Box>
              </Paper>

              {/* 스토리지 */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                스토리지
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  파일 수: {monitoring.storage?.totalFiles?.toLocaleString() || 0}개
                </Typography>
                <Typography variant="body2">
                  총 용량: {formatBytes(monitoring.storage?.totalSize || 0)}
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              데이터를 불러오는 중...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMonitoringDialog(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
